import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronRight, Store } from "lucide-react";
import { Pill } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import {
  clientStatusLabelKey,
  clientStatusPillVariant,
  formatShortDateTime,
  type PartnerClientOverviewRow,
} from "../../lib/partnerReports";
import { useI18n } from "../../i18n";

type SortKey =
  | "name"
  | "status"
  | "orders"
  | "completed"
  | "fees"
  | "charges"
  | "outstanding"
  | "last_order";

interface ClientsTableProps {
  clients: PartnerClientOverviewRow[];
  loading?: boolean;
  onOpen: (client: PartnerClientOverviewRow) => void;
  onReissue?: (client: PartnerClientOverviewRow) => void;
}

function sortValue(client: PartnerClientOverviewRow, key: SortKey): number | string {
  switch (key) {
    case "name":
      return client.name.toLowerCase();
    case "status":
      return client.tenant_status.toLowerCase();
    case "orders":
      return client.orders_count;
    case "completed":
      return client.completed_count;
    case "fees":
      return client.delivery_fees_total;
    case "charges":
      return client.client_charges_total;
    case "outstanding":
      return client.outstanding_pending;
    case "last_order":
      return client.last_order_at ? new Date(client.last_order_at).getTime() : 0;
  }
}

export function ClientsTable({ clients, loading, onOpen, onReissue }: ClientsTableProps) {
  const { t, locale } = useI18n();
  const [sortKey, setSortKey] = useState<SortKey>("last_order");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "name" ? "asc" : "desc");
  };

  const sorted = [...clients].sort((a, b) => {
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    const cmp =
      typeof av === "string" && typeof bv === "string"
        ? av.localeCompare(bv)
        : Number(av) - Number(bv);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const columns: Array<{ key: SortKey; label: string; align?: "end" }> = [
    { key: "name", label: t("clients.colClient") },
    { key: "status", label: t("clients.colStatus") },
    { key: "orders", label: t("clients.colOrders"), align: "end" },
    { key: "completed", label: t("clients.colCompleted"), align: "end" },
    { key: "fees", label: t("clients.colFees"), align: "end" },
    { key: "charges", label: t("clients.colCharges"), align: "end" },
    { key: "outstanding", label: t("clients.colOutstanding"), align: "end" },
    { key: "last_order", label: t("clients.colLastOrder") },
  ];

  return (
    <div className="report-table-wrap">
      <table className="table-flat">
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  className={col.align === "end" ? "text-end" : undefined}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className={`inline-flex items-center gap-1 uppercase tracking-[0.06em] ${
                      active ? "text-[var(--accent)]" : ""
                    }`}
                  >
                    {col.label}
                    {active ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )
                    ) : null}
                  </button>
                </th>
              );
            })}
            <th className="w-10" aria-label={t("clients.view")} />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + 1} className="py-10 text-center text-sm text-[var(--text-muted)]">
                {t("common.loading")}
              </td>
            </tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="py-10 text-center">
                <Store className="mx-auto h-7 w-7 text-[var(--text-muted)]" />
                <p className="mt-2 text-sm font-semibold text-[var(--text-secondary)]">
                  {t("clients.emptyTitle")}
                </p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  {t("clients.emptyHint")}
                </p>
              </td>
            </tr>
          ) : (
            sorted.map((client) => {
              return (
                <tr
                  key={client.id}
                  onClick={() => onOpen(client)}
                  className="cursor-pointer"
                >
                  <td>
                    <div className="flex items-center gap-2.5">
                      <span className="client-avatar" aria-hidden>
                        <Store className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-[var(--text-primary)]">
                          {client.name}
                          <span className="ms-2 text-xs font-normal text-[var(--text-muted)]">
                            {client.username ? `@${client.username}` : client.slug}
                          </span>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Pill variant={clientStatusPillVariant(client.tenant_status)}>
                      {t(clientStatusLabelKey(client.tenant_status))}
                    </Pill>
                  </td>
                  <td className="text-end">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="tabular font-semibold">{client.orders_count}</span>
                      {client.active_orders_count > 0 ? (
                        <span className="rounded-full bg-[var(--accent-light)] px-2 py-0.5 text-[11px] font-bold text-[var(--accent)]">
                          {client.active_orders_count} {t("clients.colOpen")}
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="text-end tabular">{client.completed_count}</td>
                  <td className="text-end tabular">
                    {client.delivery_fees_total > 0
                      ? formatCurrency(
                          client.delivery_fees_total,
                          client.currency,
                          locale
                        )
                      : "—"}
                  </td>
                  <td className="text-end tabular">
                    {client.client_charges_total > 0
                      ? formatCurrency(
                          client.client_charges_total,
                          client.currency,
                          locale
                        )
                      : "—"}
                  </td>
                  <td className="text-end tabular">
                    {client.outstanding_pending > 0 ? (
                      <span className="font-bold text-[var(--danger)]">
                        {formatCurrency(
                          client.outstanding_pending,
                          client.currency,
                          locale
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="whitespace-nowrap text-sm text-[var(--text-secondary)]">
                    {formatShortDateTime(client.last_order_at)}
                  </td>
                  <td className="text-end">
                    {onReissue && client.tenant_status.toLowerCase() === "onboarding" ? (
                      <button
                        type="button"
                        className="me-2 text-xs font-bold text-[var(--accent)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReissue(client);
                        }}
                      >
                        {t("clients.reissue")}
                      </button>
                    ) : null}
                    <ChevronRight className="inline h-4 w-4 text-[var(--text-muted)] rtl:rotate-180" />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
